import { createContext, useContext } from 'react';
import type { PlatformSession } from './platform-session';
import type { ScopeSnapshot } from './scope-store';
import type { Access, Identity, OrgNodeTree } from './types';

/**
 * The session as React sees it. Also what an app hands its router as context, so route guards
 * (`beforeLoad`) can read it:
 *
 * ```tsx
 * const session = useSession();
 * return <RouterProvider router={router} context={{ session }} />;
 * ```
 */
export interface SessionContextValue {
  /**
   * `pending` until `/auth/whoami` has answered; `anonymous` without a session; `error` when
   * whoami failed for another reason (network, 5xx) — not the same as logged out.
   */
  status: 'pending' | 'authenticated' | 'anonymous' | 'error';
  identity: Identity | null;
  isGlobalAdmin: boolean;
  /** The selected org node and view mode. Change it through `session.scope`. */
  scope: ScopeSnapshot;
  /** The nodes the user may select; undefined while loading. */
  orgTree: OrgNodeTree | undefined;
  /** Effective permissions and entitlements at the selected node; undefined while loading. */
  access: Access | undefined;
  /** Log in, then load the new user's identity. Rejects with the ApiError the backend sent. */
  login(credentials: { email: string; password: string }): Promise<Identity | null>;
  /** Log out on the server and forget everything this tab knew about the user. */
  logout(): Promise<void>;
  /** The objects behind it all: the clients, the scope store (push/pop/resetTo/setViewMode). */
  platform: PlatformSession;
}

export const SessionContext = createContext<SessionContextValue | null>(null);

export function useSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (!value) {
    throw new Error('useSession must be used inside <PlatformProvider>');
  }
  return value;
}
